import { useEffect, useState } from "react";
import { X, History, LogIn, LogOut, User, Ban } from "lucide-react";
import clienteApi from "@/lib/clienteApi";

/**
 * Modal somente-leitura: histórico de movimentos (entradas/saídas) de um visitante.
 * Usado em visitantes do tipo hóspede que entram e saem múltiplas vezes durante a validade.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - visitante: { id, nome, cpf, ... }
 *  - onFinalizarCiclo?: (visitante) => void  -- opcional: ação no header para encerrar o ciclo
 */
export default function HistoricoMovimentosVisitanteModal({ open, onClose, visitante, onFinalizarCiclo }) {
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !visitante?.id) return;
    setLoading(true);
    clienteApi
      .get(`/cliente/visitantes/${visitante.id}/movimentos`)
      .then((r) => setMovs(r.data || []))
      .catch(() => setMovs([]))
      .finally(() => setLoading(false));
  }, [open, visitante?.id]);

  if (!open || !visitante) return null;

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const qtdEntradas = movs.filter((m) => m.tipo === "ENTRADA").length;
  const qtdSaidas = movs.filter((m) => m.tipo === "SAIDA").length;

  return (
    <div
      data-testid="historico-movimentos-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-brand-deep border border-brand-ice/20 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-ice/15">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-brand-light" />
            <div>
              <div className="text-xs uppercase tracking-wider text-brand-ice/60">
                Histórico de Movimentos
              </div>
              <div className="font-heading font-bold text-brand-ice text-base">
                {visitante.nome}
                <span className="ml-3 text-xs font-normal text-brand-ice/60">
                  {qtdEntradas} entrada(s) · {qtdSaidas} saída(s)
                </span>
                {visitante.status_atual && (
                  <span
                    className={`ml-3 inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                      visitante.status_atual === "DENTRO"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-slate-500/20 text-slate-300"
                    }`}
                  >
                    {visitante.status_atual}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Botão Finalizar Ciclo — apenas se o ciclo não foi finalizado e callback foi passado */}
            {onFinalizarCiclo && !visitante.ciclo_finalizado && (
              <button
                data-testid="historico-finalizar-ciclo-btn"
                onClick={() => onFinalizarCiclo(visitante)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-300 hover:text-amber-200 text-xs font-heading font-bold transition-colors"
                title="Encerrar o ciclo deste visitante (impede novos movimentos)"
              >
                <Ban className="w-3.5 h-3.5" />
                Finalizar
              </button>
            )}
            <button
              data-testid="close-historico-mov"
              onClick={onClose}
              className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-brand-ice/50 text-sm">Carregando movimentos...</div>
          ) : movs.length === 0 ? (
            <div className="text-center py-8 text-brand-ice/50 text-sm">
              Nenhum movimento registrado ainda.
            </div>
          ) : (
            <div className="space-y-2">
              {movs.map((m) => {
                const isEntrada = m.tipo === "ENTRADA";
                return (
                  <div
                    key={m.id}
                    data-testid={`mov-${m.id}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      isEntrada
                        ? "bg-green-500/5 border-green-400/20"
                        : "bg-red-500/5 border-red-400/20"
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                        isEntrada
                          ? "bg-green-500/20 text-green-300"
                          : "bg-red-500/20 text-red-300"
                      }`}
                    >
                      {isEntrada ? <LogIn className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-sm font-heading font-bold ${
                            isEntrada ? "text-green-300" : "text-red-300"
                          }`}
                        >
                          {isEntrada ? "Entrada" : "Saída"}
                        </span>
                        <span className="font-mono text-xs text-brand-ice/80">{fmt(m.data_hora)}</span>
                        {m.origem && m.origem !== "MANUAL" && (
                          <span className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] uppercase tracking-wider text-brand-ice/60">
                            {m.origem}
                          </span>
                        )}
                      </div>
                      {m.operador_nome && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-brand-ice/50">
                          <User className="w-3 h-3" />
                          <span>{m.operador_nome}</span>
                          {m.portaria_nome && <span className="text-brand-ice/30">· {m.portaria_nome}</span>}
                        </div>
                      )}
                      {m.obs && (
                        <p className="text-xs text-brand-ice/70 mt-1 italic">"{m.obs}"</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-brand-ice/15 flex justify-end">
          <button
            data-testid="fechar-historico-mov"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-brand-ice font-heading font-bold text-sm transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
